import { useGraphQLQuery } from '@betterment/graphql';

import { ProductCategoryOptionsOrder } from '../../graphql/schema';
import {
  HomeProductOnboardingOptionsDocument,
  HomeProductOnboardingOptionsQuery,
} from '../graphql/homeProductOnboardingOptions.generated';
import { HomeProductBundlesQuery } from '../graphql/productBundles.generated';

export type CheckingOptionInManualReview = HomeProductOnboardingOptionsQuery['productOnboardingOptions'][number] & {
  __typename: 'CheckingAccountCreationOption';
};

type UseProductOnboardingOptionsParams = {
  order?: ProductCategoryOptionsOrder;
  bundles?: HomeProductBundlesQuery['productBundles'];
};

type ProductOption = HomeProductOnboardingOptionsQuery['productOnboardingOptions'][number];
type Bundle = HomeProductBundlesQuery['productBundles'][number];

const filterOptionsNotInBundles = (options: ProductOption[], bundles: Bundle[] = []): ProductOption[] => {
  const bundledProductIds = new Set(
    bundles.filter(bundle => bundle.isInProgress).flatMap(bundle => bundle.productOnboardingOptions.map(p => p.id))
  );

  return options.filter(option => !bundledProductIds.has(option.id));
};

const filterOptionsWithoutScheduledMeetings = (options: ProductOption[]): ProductOption[] => {
  return options.filter(o => !('hasScheduledMeeting' in o) || !o.hasScheduledMeeting);
};

const processIntents = (data: HomeProductOnboardingOptionsQuery, optionsNotInBundles: ProductOption[]) => {
  const goalCreationIntents = data.goalCreationIntents.filter(i => i.isUserSelected);

  const checkingReceivedInvitations = data.checkingReceivedInvitations.filter(i =>
    ['INVITE_SENT', 'ACCOUNT_CREATED'].includes(i.status)
  );

  const checkingOptionInManualReview = optionsNotInBundles
    .filter(o => o.__typename === 'CheckingAccountCreationOption')
    .find(o => o.isInManualReview) as CheckingOptionInManualReview | undefined;

  return {
    goalCreationIntents,
    checkingReceivedInvitations,
    checkingOptionInManualReview,
  };
};

export const useProductOnboardingOptions = (params?: UseProductOnboardingOptionsParams) => {
  const { order, bundles } = params || {};

  const { data } = useGraphQLQuery(HomeProductOnboardingOptionsDocument, {
    throwOnError: true,
    variables: { ...(order ? { orderingOption: order } : {}) },
  });

  if (!data) return { data: undefined };

  const { productOnboardingOptions: options, envelopes } = data;
  const optionsNotInBundles = filterOptionsNotInBundles(options, bundles);
  const hasEnvelopes = !!envelopes.length;
  const intents = processIntents(data, optionsNotInBundles);

  if (hasEnvelopes) {
    return {
      data: {
        crossSellOptions: optionsNotInBundles,
        inProgressOptions: [],
        notInProgressOptions: [],
        intents,
      },
    };
  }

  const initialOptions = filterOptionsWithoutScheduledMeetings(optionsNotInBundles);
  const hasGoalIntents = intents.goalCreationIntents.length > 0;

  return {
    data: {
      crossSellOptions: [],
      inProgressOptions: hasGoalIntents ? [] : initialOptions.filter(o => o.isInProgress),
      notInProgressOptions: initialOptions.filter(o => !o.isInProgress),
      intents,
    },
  };
};
